<template>
  <div class="reportWrapper">
    <div class="report-component" id="tr-indicator-type-b-html">
      <div class="report-component-container">
        <h1 class="report-component-title">{{ title }}</h1>
        <p class="report-component-definition">{{ doscodeDefinition }}</p>
      </div>
      <p class="report-component-section">{{ section }}</p>
      <p class="report-component-date">Published {{ publishDate }}</p>
      <div class="two-col-wrapper">
        <div
          class="col-inner background-grey-soft inner-padding rounded-corners-10"
        >
          <h4>HIGHLIGHTS</h4>
          <div class="highlights-text" v-html="cwaData.Highlights"></div>

          <div v-if="cwaData.ForecastTable" class="pt-5">
            <h4>{{ cwaData.ForecastTable.TableHeader }}</h4>
            <div
              v-if="cwaData.ForecastTable.RowData.length > 0"
              class="table-responsive"
            >
              <table class="tr-data-table darker-borders">
                <tbody>
                  <tr
                    v-for="(value, index) in cwaData.ForecastTable.RowData"
                    :key="index"
                  >
                    <td
                      class="table-td"
                      v-for="(data, dataIndex) in value"
                      :key="dataIndex"
                      :style="getStyle(index, 'ForecastTable')"
                    >
                      {{
                        data === '' && (dataIndex !== 0 || index !== 0)
                          ? 'N/A'
                          : data
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="cwaData.ShowForecastRange" class="pt-5">
            <h4>{{ cwaData.ForecastRangeTable.TableHeader }}</h4>
            <div
              v-if="cwaData.ForecastRangeTable.RowData.length > 0"
              class="table-responsive"
            >
              <table class="tr-data-table darker-borders">
                <tbody>
                  <tr
                    v-for="(value, index) in cwaData.ForecastRangeTable.RowData"
                    :key="index"
                  >
                    <td
                      class="table-td__foreacast"
                      v-for="(data, dataIndex) in value"
                      :key="dataIndex"
                      :style="getStyle(index, 'ForecastRangeTable')"
                    >
                      {{ data === '' ? ' N/A' : addSpaceToData(data) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="pt-5">
            <h4>OVERVIEW</h4>
            <div class="highlights-text" v-html="cwaData.Overview"></div>
          </div>
        </div>
        <div class="col-inner inner-padding-top">
          <div class="charts">
            <h4>RATE-OF-CHANGE</h4>
            <TrendsReportROCChart
              :rocData="cwaData.RoCChart"
              :fromReport="true"
            />
            <br />
          </div>
          <div class="charts">
            <h4>DATA TREND</h4>
            <TrendsReportDTChart
              v-if="cwaData.DataTrendsChart"
              :dtData="cwaData.DataTrendsChart"
              :fromReport="true"
            />
          </div>
          <div
            class="tr-phase-box-flex"
            v-bind:class="phaseClass"
            v-if="phaseClass"
          >
            <div class="tr-phase-box-flex__left">
              <img
                :src="asset('/images/tr-' + phaseClass + '.png')"
                alt="Trends Report Phase Logo"
              />
            </div>
            <div class="tr-phase-box-flex__right">
              <h4>ITR MANAGEMENT OBJECTIVE</h4>
              <div class="management-objective" v-html="cwaData.ManagementObjectives"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, ref, onMounted } from 'vue';
  import TrendsReportROCChart from '~/components/trendsreport/TrendsReportROCChart.vue';
  import TrendsReportDTChart from '~/components/trendsreport/TrendsReportDTChart.vue';

  export default {
    name: 'TrIndicatorTypeBTemplate',
    components: {
      TrendsReportROCChart,
      TrendsReportDTChart,
    },
    props: {
      title: {
        type: String,
        required: true,
      },
      cwaData: {
        type: Object,
        required: true,
      },
      section: {
        type: String,
        required: false,
      },
      publishDate: {
        type: String,
        required: false,
      },
    },

    setup(props) {
      const assetUrl = ref('');

      onMounted(() => {
        assetUrl.value = document
          .getElementById('main-page-wrapper')
          .getAttribute('data-asset-url');
      });

      const asset = (path) => {
        return `${assetUrl.value.replace(/\/$/, '')}${path}`;
      };

      let phaseClass = computed(() => {
        if (props.cwaData.Phase === 'A') {
          return 'phase-a';
        } else if (props.cwaData.Phase === 'B') {
          return 'phase-b';
        } else if (props.cwaData.Phase === 'C') {
          return 'phase-c';
        } else if (props.cwaData.Phase === 'D') {
          return 'phase-d';
        } else {
          return null;
        }
      });

      let doscodeDefinition = computed(() => {
        const definition = props.cwaData.DOSCodeDefinition;
        const definitionArray = definition.split('.');
        let finalSentence = definitionArray.pop() || definitionArray.pop();
        return finalSentence || '';
      });

      let getStyle = (index, tableType) => {
        if (index !== 0) return '';
        if (tableType === 'ForecastTable') {
          if (
            index === 0 &&
            props.cwaData.ForecastTable.RowData[0].length > 2
          ) {
            return 'font-weight:bold';
          }
        }
        if (tableType === 'ForecastRangeTable') {
          if (
            index === 0 &&
            props.cwaData.ForecastRangeTable.RowData[0].length > 2
          ) {
            return 'font-weight:bold';
          }
        }
      };

      const addSpaceToData = (data) => {
        let spacedData = data
          .split(' ')
          .map((item) => ` ${item} `)
          .join('');
        return spacedData;
      };

      return {
        asset,
        phaseClass,
        doscodeDefinition,
        getStyle,
        addSpaceToData,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .reportWrapper {
    // margin: 3rem 0;
    page-break-inside: avoid;
    font-family: 'Open Sans', sans-serif !important;
  }
  .report-component-container {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    gap: 4rem;
    margin-bottom: 0;
  }
  .report-component-definition {
    font-size: 20px;
    font-style: italic;
    max-width: 40%;
  }
  .report-component-section {
    color: var(--itr-blue);
    font-weight: bold;
    font-size: 26px;
    margin-top: 1rem;
  }
  .report-component-date {
    color: var(--itr-grey-charcoal);
    font-size: 22px;
    font-style: italic;
    margin: 1rem 0;
  }
  h4 {
    text-transform: capitalize;
  }
  .tr-phase-box-flex {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: nowrap !important;
    gap: 2rem;
    border-radius: 10px;
    padding: 40px;
    margin-top: 4rem;
    img {
      max-width: 120px;
    }
    &__left {
      color: var(--itr-white);
    }
    &__right {
      color: var(--itr-white);
      h4 {
        color: var(--itr-white);
        margin-top: 0;
      }
      p {
        font-size: 20px;
      }
    }
  }
  .phase-a {
    background-color: var(--itr-tr-blue);
  }
  .phase-b {
    background-color: var(--itr-tr-green);
  }
  .phase-c {
    background-color: var(--itr-tr-orange);
  }
  .phase-d {
    background-color: var(--itr-tr-red);
  }
  .two-col-wrapper {
    display: flex;
    flex-wrap: nowrap;
    padding-right: 1rem;
    gap: 6rem;
    margin-top: 2rem;
  }
  .background-grey-soft {
    // background: var(--itr-grey-soft);
    background: #eaeff2;
  }
  .inner-padding {
    padding: 25px 40px;
  }
  .rounded-corners-10 {
    border-radius: 10px;
  }
  h1 {
    color: var(--itr-blue);
    font-size: 40px;
    margin-bottom: 0px;
  }
  h4 {
    font-size: 20px;
    line-height: 1.3em;
    margin-bottom: 12px;
    color: var(--itr-blue);
    font-weight: 800;
  }
  .col-inner {
    width: calc(50% - 40px);
    @media (min-width: 1600px) {
      width: calc(50% - 60px);
    }
    @media (min-width: 1400px) {
      width: calc(50% - 50px);
    }
    @media (min-width: 1200px) {
      width: calc(50% - 50px);
    }
    @media (min-width: 1000px) {
      width: calc(50% - 40px);
    }
  }
  .table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
  table.tr-data-table {
    padding: 0;
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }
  table.tr-data-table.darker-borders tr {
    border-bottom: 1px solid var(--itr-grey);
  }
  table.tr-data-table tbody tr td {
    padding: 10px 15px;
    font-size: 18px;
  }
  .table-td {
    width: 10%;
    font-size: 18px;
    &__foreacast {
      white-space: pre !important;
    }
  }
  .highlights-text{
    * {
      font-size: 17px !important;
      color: var(--itr-charcoal) !important;
      background-color: transparent !important;
      font-family: 'Open Sans', sans-serif !important;
    }
  }
  .management-objective {
    * {
      font-size: 17px !important;
      color: var(--itr-white) !important;
      background-color: transparent !important;
      font-family: 'Open Sans', sans-serif !important;
    }
  }
</style>
